"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bandSetPublishedSchema = exports.bandSetPublishInputSchema = exports.bandSetParamsSchema = exports.bandSetRangesSchema = exports.bandVitalDefinitionSchema = exports.bandSetListSchema = exports.bandSetItemSchema = exports.bandSetStatusSchema = void 0;
/**
 * `RPM-058` and `RPM-059` — the band-set editor's three routes (`J-292`, `J-293`).
 *
 *   `GET  /api/rpm/v1/admin/band-sets`                     the tenant's versions, newest first
 *   `GET  /api/rpm/v1/admin/band-sets/:bandSetId/ranges`   one version as the editor reads it
 *   `POST /api/rpm/v1/admin/band-sets`                     publish: a create, never an update
 *
 * A PUBLISH IS A NEW VERSION. Nothing here names a band row to change: the body is the whole
 * partition, as ranges (`band-partition.ts`), and the server writes the threshold rows it implies
 * under a new set that supersedes the current one by pointer.
 *
 * WHEN IT TAKES EFFECT (`RPM-059`, `D-1387`). `validFrom` absent is Immediately; present, it is the
 * instant the version becomes current, read in the tenant's default zone on the surface
 * (`tenantTimeZone`, `D-1539`). There is no day boundary for a band set.
 */
const zod_1 = require("zod");
const band_partition_js_1 = require("./band-partition.js");
const rpm_blast_radius_js_1 = require("./rpm-blast-radius.js");
/** Where a version stands now: in force, waiting for its `effectiveFrom`, or closed by a successor. */
exports.bandSetStatusSchema = zod_1.z
    .enum(["CURRENT", "SCHEDULED", "SUPERSEDED"])
    .meta({ id: "BandSetStatus", description: "Where a band-set version stands now" });
/** One version, as the list and the editor name it. Instants are ISO 8601. */
exports.bandSetItemSchema = zod_1.z
    .object({
    classificationBandSetId: zod_1.z.uuid(),
    bandSetVersion: zod_1.z.number().int().positive(),
    label: zod_1.z.string(),
    effectiveFrom: zod_1.z.iso.datetime({ offset: true }),
    effectiveTo: zod_1.z.iso.datetime({ offset: true }).nullable(),
    publishedAt: zod_1.z.iso.datetime({ offset: true }),
    status: exports.bandSetStatusSchema,
})
    .meta({ id: "BandSetItem", description: "One published band-set version" });
/** `GET /api/rpm/v1/admin/band-sets`. */
exports.bandSetListSchema = zod_1.z
    .object({
    items: zod_1.z.array(exports.bandSetItemSchema),
    /** The tenant's `default_time_zone`: the zone an Effective-from time is read in (`D-1539`). */
    tenantTimeZone: zod_1.z.string(),
})
    .meta({ id: "BandSetList", description: "The tenant's band-set versions, newest first" });
/** What the editor needs of one classified vital: its name, unit, precision and plausible span. */
exports.bandVitalDefinitionSchema = zod_1.z
    .object({
    vitalCode: zod_1.z.enum(band_partition_js_1.BAND_VITAL_CODES),
    displayLabel: zod_1.z.string(),
    /** The comparison unit the bands are in, as a UCUM code. */
    ucumUnit: zod_1.z.string(),
    /** Decimal places a value is recorded at; the editor's step is one in the last place. */
    decimals: zod_1.z.number().int().nonnegative(),
    plausibleMin: zod_1.z.string(),
    plausibleMax: zod_1.z.string(),
})
    .meta({ id: "BandVitalDefinition", description: "One classified vital, as the editor reads it" });
/** `GET /api/rpm/v1/admin/band-sets/:bandSetId/ranges`. */
exports.bandSetRangesSchema = zod_1.z
    .object({
    bandSet: exports.bandSetItemSchema,
    ranges: zod_1.z.array(band_partition_js_1.bandRangeSchema),
    definitions: zod_1.z.array(exports.bandVitalDefinitionSchema),
})
    .meta({ id: "BandSetRanges", description: "One band-set version as ranges, with its vitals" });
/** The path of the ranges route. No `.meta({ id })` on a params schema (`index.ts`'s note). */
exports.bandSetParamsSchema = zod_1.z.object({ bandSetId: zod_1.z.uuid() });
/**
 * `POST /api/rpm/v1/admin/band-sets`.
 *
 * `statedReason` IS OPTIONAL IN THE SHAPE AND REQUIRED BY THE ROUTE: absent or blank is
 * `422 STATED_REASON_REQUIRED`, the code the routing writes use, rather than a `400` that says only
 * that the body did not match.
 *
 * `scope` AND `scopeFacilityId` ARE ACCEPTED SO THAT THEY CAN BE REFUSED BY NAME. A band set is
 * tenant-wide (`RPM-086`); a facility-scoped publish is `422` naming the extension point, not a
 * `400` for an unknown field.
 *
 * `supersedesBandSetId` IS ABSENT ONLY FOR A TENANT'S FIRST VERSION (`RPM-064`, `03-RPM-SCHEMA.md`
 * §10.4 step 5): a tenant just provisioned holds no set to supersede. Absent in a tenant that holds
 * any set is `409`, the answer a stale one gets.
 */
exports.bandSetPublishInputSchema = zod_1.z
    .strictObject({
    /**
     * The set in force when the editor opened it. `409` when it is no longer the current one.
     * Absent: the tenant's first version, `409` when the tenant already holds a set.
     */
    supersedesBandSetId: zod_1.z.uuid().optional(),
    label: zod_1.z.string().trim().min(1).max(200),
    ranges: zod_1.z.array(band_partition_js_1.bandRangeSchema).min(1).max(200),
    statedReason: zod_1.z.string().max(1000).optional(),
    /** Absent: Immediately. Present: the instant it becomes current, never in the past. */
    validFrom: zod_1.z.iso.datetime({ offset: true }).optional(),
    blastRadiusShown: rpm_blast_radius_js_1.blastRadiusShownSchema,
    scope: zod_1.z.enum(["TENANT", "FACILITY"]).optional(),
    scopeFacilityId: zod_1.z.string().min(1).max(20).optional(),
})
    .meta({ description: "A new band-set version, why, when it takes effect, and what was shown" });
/** What a publish wrote, and the open-alert count it recorded (`THRESHOLD_CHANGE_WITH_ALERTS_IN_FLIGHT`). */
exports.bandSetPublishedSchema = zod_1.z
    .object({
    bandSet: exports.bandSetItemSchema,
    /** Alerts in `CLASSIFIED`, `BLOCKED` or `NOTIFY_OWED` in the tenant at the publish. */
    openAlertCount: zod_1.z.number().int().nonnegative(),
})
    .meta({ id: "BandSetPublished", description: "The version a publish wrote" });
//# sourceMappingURL=rpm-band-sets.js.map